import { useState } from 'react';
import {
  Button, Chip, Stack, TextField, MenuItem, Dialog, DialogTitle, DialogContent, DialogActions, Typography,
  Alert, Snackbar, IconButton, Tooltip, Table, TableHead, TableBody, TableRow, TableCell, Box, CircularProgress, Divider,
} from '@mui/material';
import { Add, AutoAwesome, Delete, Visibility } from '@mui/icons-material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { PageHeader } from '../../components/PageHeader';
import { DataTable, type Column } from '../../components/DataTable';
import { FileUploadField } from '../../components/FileUploadField';
import { purchasesApi, warehousesApi, type Purchase, type PurchaseLine, type PurchaseCharge } from '../../api/inventory.api';
import { extractApiError } from '../../api/error';
import { useAuth } from '../../hooks/useAuth';

const inr = (n = 0) => `₹${Number(n).toLocaleString('en-IN')}`;
const emptyLine = (): PurchaseLine => ({ itemName: '', category: '', brand: '', uom: 'nos', hsnCode: '', gstPercent: null, quantity: 1, rate: null });
const emptyCharge = (): PurchaseCharge => ({ label: '', amount: null, gstPercent: null });

export function PurchasesPage() {
  const qc = useQueryClient();
  const { hasPermission } = useAuth();
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(25);
  const [open, setOpen] = useState(false);
  const [detailId, setDetailId] = useState<number | null>(null);
  const [toast, setToast] = useState<string | null>(null);

  const { data, isLoading } = useQuery({ queryKey: ['inv-purchases', page, limit], queryFn: () => purchasesApi.list({ page, limit }) });
  const canCreate = hasPermission('inventory.transact');

  const columns: Column<Purchase>[] = [
    { key: 'purchaseCode', label: 'Purchase' },
    { key: 'supplierName', label: 'Supplier', render: (r) => r.supplierName ?? '—' },
    { key: 'invoiceNumber', label: 'Invoice', render: (r) => r.invoiceNumber ?? '—' },
    { key: 'invoiceDate', label: 'Date', render: (r) => (r.invoiceDate ? new Date(r.invoiceDate).toLocaleDateString('en-IN') : '—') },
    { key: 'totalAmount', label: 'Total', align: 'right', render: (r) => inr(r.totalAmount) },
    { key: 'status', label: 'Status', render: (r) => <Chip size="small" color="success" label={r.status} /> },
    { key: 'actions', label: '', width: 60, render: (r) => <Tooltip title="View"><IconButton size="small" onClick={() => setDetailId(r.id)}><Visibility fontSize="small" /></IconButton></Tooltip> },
  ];

  return (
    <>
      <PageHeader title="Purchases / Goods Inward" subtitle="Upload a supplier invoice → auto-create Item Master entries & post warehouse stock."
        action={canCreate && <Button startIcon={<Add />} variant="contained" onClick={() => setOpen(true)}>New Purchase</Button>} />
      <DataTable columns={columns} rows={data?.data ?? []} loading={isLoading} page={page} limit={limit} total={data?.meta.total ?? 0}
        onPageChange={setPage} onLimitChange={(l) => { setLimit(l); setPage(1); }} rowKey={(r) => r.id} />

      {open && <PurchaseDialog onClose={() => setOpen(false)} onDone={() => { setOpen(false); setToast('Purchase recorded — items & stock updated'); qc.invalidateQueries({ queryKey: ['inv-purchases'] }); }} />}
      {detailId && <DetailDialog id={detailId} onClose={() => setDetailId(null)} />}
      <Snackbar open={Boolean(toast)} autoHideDuration={4000} onClose={() => setToast(null)}><Alert severity="success" onClose={() => setToast(null)}>{toast}</Alert></Snackbar>
    </>
  );
}

function PurchaseDialog({ onClose, onDone }: { onClose: () => void; onDone: () => void }) {
  const [form, setForm] = useState({
    supplierName: '', supplierGstin: '', supplierPan: '', supplierEmail: '', supplierPhone: '', supplierCategory: '',
    invoiceNumber: '', invoiceDate: '', invoiceUrl: '', warehouseId: '' as number | '', purchaseOrderId: '' as number | '',
    shipFrom: '', shipTo: '', transporterName: '', vehicleNo: '', lrNumber: '', ewayBillNo: '', dispatchDate: '',
  });
  const [lines, setLines] = useState<PurchaseLine[]>([emptyLine()]);
  const [charges, setCharges] = useState<PurchaseCharge[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);

  const { data: warehouses } = useQuery({ queryKey: ['warehouses-lookup'], queryFn: () => warehousesApi.list({ limit: 200 }) });
  const { data: openPos } = useQuery({ queryKey: ['open-purchase-orders'], queryFn: () => purchasesApi.openPurchaseOrders() });
  const setLine = (i: number, k: keyof PurchaseLine, v: unknown) => setLines((arr) => arr.map((l, j) => (j === i ? { ...l, [k]: v } : l)));
  const setCharge = (i: number, k: keyof PurchaseCharge, v: unknown) => setCharges((arr) => arr.map((c, j) => (j === i ? { ...c, [k]: v } : c)));

  const extractMut = useMutation({
    mutationFn: () => purchasesApi.extract(form.invoiceUrl),
    onSuccess: (r) => {
      setError(null);
      if (!r.configured) { setInfo(r.message ?? 'AI extraction not configured — enter items manually.'); return; }
      if (!r.lines.length && !r.charges.length) { setInfo(r.message ?? 'No line items detected — enter them manually.'); return; }
      const chargeCount = r.charges?.length ?? 0;
      setInfo(`Extracted ${r.lines.length} material line(s)${chargeCount ? ` + ${chargeCount} charge(s)` : ''} — review & edit below.`);
      if (r.lines.length) setLines(r.lines.map((l) => ({ ...emptyLine(), ...l, quantity: l.quantity ?? 1 })));
      if (chargeCount) setCharges(r.charges.map((c) => ({ ...emptyCharge(), ...c })));
      // Prefill supplier/vendor + invoice header + shipment from the extraction (only empty fields).
      setForm((f) => ({
        ...f,
        supplierName: f.supplierName || (r.supplierName ?? ''),
        supplierGstin: f.supplierGstin || (r.supplierGstin ?? ''),
        supplierPan: f.supplierPan || (r.supplierPan ?? ''),
        supplierEmail: f.supplierEmail || (r.supplierEmail ?? ''),
        supplierPhone: f.supplierPhone || (r.supplierPhone ?? ''),
        supplierCategory: f.supplierCategory || (r.supplierCategory ?? ''),
        invoiceNumber: f.invoiceNumber || (r.invoiceNumber ?? ''),
        invoiceDate: f.invoiceDate || (r.invoiceDate ?? ''),
        shipFrom: f.shipFrom || (r.shipFrom ?? ''),
        shipTo: f.shipTo || (r.shipTo ?? ''),
        transporterName: f.transporterName || (r.transporterName ?? ''),
        vehicleNo: f.vehicleNo || (r.vehicleNo ?? ''),
        lrNumber: f.lrNumber || (r.lrNumber ?? ''),
        ewayBillNo: f.ewayBillNo || (r.ewayBillNo ?? ''),
        // Don't echo the invoice date as the dispatch date — only keep a genuinely distinct dispatch date.
        dispatchDate: f.dispatchDate || (r.dispatchDate && r.dispatchDate !== r.invoiceDate ? r.dispatchDate : ''),
      }));
    },
    onError: (e) => setError(extractApiError(e)),
  });

  const saveMut = useMutation({
    mutationFn: () => purchasesApi.create({
      supplierName: form.supplierName || undefined, supplierGstin: form.supplierGstin || undefined,
      supplierPan: form.supplierPan || undefined, supplierEmail: form.supplierEmail || undefined, supplierPhone: form.supplierPhone || undefined,
      supplierCategory: form.supplierCategory || undefined,
      invoiceNumber: form.invoiceNumber || undefined,
      invoiceDate: form.invoiceDate || undefined, invoiceUrl: form.invoiceUrl || undefined,
      warehouseId: Number(form.warehouseId),
      purchaseOrderId: form.purchaseOrderId ? Number(form.purchaseOrderId) : null,
      shipFrom: form.shipFrom || undefined, shipTo: form.shipTo || undefined, transporterName: form.transporterName || undefined,
      vehicleNo: form.vehicleNo || undefined, lrNumber: form.lrNumber || undefined, ewayBillNo: form.ewayBillNo || undefined,
      dispatchDate: form.dispatchDate || undefined,
      lines: lines.filter((l) => l.itemName.trim()).map((l) => ({ ...l, quantity: Number(l.quantity) || 0, rate: l.rate != null ? Number(l.rate) : null, gstPercent: l.gstPercent != null ? Number(l.gstPercent) : null })),
      charges: charges.filter((c) => c.label.trim()).map((c) => ({ label: c.label.trim(), amount: c.amount != null ? Number(c.amount) : null, gstPercent: c.gstPercent != null ? Number(c.gstPercent) : null })),
    }),
    onSuccess: onDone, onError: (e) => setError(extractApiError(e)),
  });

  const validLines = lines.filter((l) => l.itemName.trim());
  const validCharges = charges.filter((c) => c.label.trim());
  const lineTotal = validLines.reduce((s, l) => s + (Number(l.quantity) || 0) * (Number(l.rate) || 0), 0);
  const chargesTotal = validCharges.reduce((s, c) => s + (Number(c.amount) || 0), 0);
  // GST matches the invoice: tax on each material line + each charge.
  const lineTax = validLines.reduce((s, l) => s + (Number(l.quantity) || 0) * (Number(l.rate) || 0) * (Number(l.gstPercent) || 0) / 100, 0);
  const chargeTax = validCharges.reduce((s, c) => s + (Number(c.amount) || 0) * (Number(c.gstPercent) || 0) / 100, 0);
  const subTotal = lineTotal + chargesTotal;
  const taxTotal = lineTax + chargeTax;
  const total = subTotal + taxTotal;
  const canSave = Boolean(form.warehouseId) && validLines.length > 0;

  return (
    <Dialog open fullWidth maxWidth="lg" onClose={onClose}>
      <DialogTitle>New Purchase / Goods Inward</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2} mt={0.5}>
          {error && <Alert severity="error" onClose={() => setError(null)}>{error}</Alert>}
          <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
            <TextField size="small" label="Vendor / Supplier" value={form.supplierName} onChange={(e) => setForm({ ...form, supplierName: e.target.value })} sx={{ minWidth: 200 }} helperText="New vendor auto-onboarded on save" />
            <TextField size="small" label="Vendor GSTIN" value={form.supplierGstin} onChange={(e) => setForm({ ...form, supplierGstin: e.target.value.toUpperCase() })} sx={{ minWidth: 170 }} />
            <TextField size="small" label="Vendor PAN" value={form.supplierPan} onChange={(e) => setForm({ ...form, supplierPan: e.target.value.toUpperCase() })} sx={{ minWidth: 130 }} helperText="Auto from GSTIN if blank" />
            <TextField size="small" label="Vendor Email" value={form.supplierEmail} onChange={(e) => setForm({ ...form, supplierEmail: e.target.value })} sx={{ minWidth: 180 }} />
            <TextField size="small" label="Vendor Phone" value={form.supplierPhone} onChange={(e) => setForm({ ...form, supplierPhone: e.target.value })} sx={{ minWidth: 140 }} />
            <TextField size="small" label="Vendor Category" value={form.supplierCategory} onChange={(e) => setForm({ ...form, supplierCategory: e.target.value })} sx={{ minWidth: 170 }} helperText="Auto-detected from invoice" />
          </Stack>
          <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
            <TextField size="small" label="Invoice No." value={form.invoiceNumber} onChange={(e) => setForm({ ...form, invoiceNumber: e.target.value })} sx={{ minWidth: 160 }} />
            <TextField size="small" type="date" label="Invoice Date" InputLabelProps={{ shrink: true }} value={form.invoiceDate} onChange={(e) => setForm({ ...form, invoiceDate: e.target.value })} />
            <TextField select size="small" label="Warehouse *" value={form.warehouseId} onChange={(e) => setForm({ ...form, warehouseId: e.target.value ? Number(e.target.value) : '' })} sx={{ minWidth: 200 }}>
              {(warehouses?.data ?? []).map((w) => <MenuItem key={w.id} value={w.id}>{w.name}</MenuItem>)}
            </TextField>
            <TextField select size="small" label="Purchase Order (open)" value={form.purchaseOrderId}
              onChange={(e) => {
                const poId = e.target.value ? Number(e.target.value) : '';
                const po = (openPos ?? []).find((p) => p.id === poId);
                // Map the PO and prefill the vendor name if not already entered.
                setForm((f) => ({ ...f, purchaseOrderId: poId, supplierName: f.supplierName || (po?.vendorName ?? '') }));
              }}
              sx={{ minWidth: 240 }} helperText="Maps this goods-inward to an open PO">
              <MenuItem value="">— None —</MenuItem>
              {(openPos ?? []).map((p) => <MenuItem key={p.id} value={p.id}>{p.poCode}{p.vendorName ? ` · ${p.vendorName}` : ''}{p.rfqCode ? ` (${p.rfqCode})` : ''}</MenuItem>)}
            </TextField>
          </Stack>

          <Divider textAlign="left"><Typography variant="caption" color="text.secondary">Shipment / Freight — where it was sent from & to</Typography></Divider>
          <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
            <TextField size="small" label="Ship From (origin)" value={form.shipFrom} onChange={(e) => setForm({ ...form, shipFrom: e.target.value })} sx={{ minWidth: 220 }} />
            <TextField size="small" label="Ship To (destination)" value={form.shipTo} onChange={(e) => setForm({ ...form, shipTo: e.target.value })} sx={{ minWidth: 220 }} />
            <TextField size="small" label="Transporter" value={form.transporterName} onChange={(e) => setForm({ ...form, transporterName: e.target.value })} sx={{ minWidth: 180 }} />
            <TextField size="small" label="Vehicle No." value={form.vehicleNo} onChange={(e) => setForm({ ...form, vehicleNo: e.target.value.toUpperCase() })} sx={{ minWidth: 130 }} />
            <TextField size="small" label="LR / Docket No." value={form.lrNumber} onChange={(e) => setForm({ ...form, lrNumber: e.target.value })} sx={{ minWidth: 140 }} />
            <TextField size="small" label="E-Way Bill No." value={form.ewayBillNo} onChange={(e) => setForm({ ...form, ewayBillNo: e.target.value })} sx={{ minWidth: 150 }} />
            <TextField size="small" type="date" label="Dispatch Date" InputLabelProps={{ shrink: true }} value={form.dispatchDate} onChange={(e) => setForm({ ...form, dispatchDate: e.target.value })} />
          </Stack>

          <Box sx={{ bgcolor: 'action.hover', borderRadius: 1.5, p: 1.5 }}>
            <Stack direction="row" spacing={2} alignItems="flex-start" flexWrap="wrap" useFlexGap>
              <Box sx={{ flex: 1, minWidth: 260 }}>
                <FileUploadField label="Supplier Invoice (PDF/image)" accept=".pdf,image/*" value={form.invoiceUrl} onChange={(url) => setForm({ ...form, invoiceUrl: url ?? '' })} />
              </Box>
              <Button startIcon={extractMut.isPending ? <CircularProgress size={16} /> : <AutoAwesome />} variant="outlined"
                disabled={!form.invoiceUrl || extractMut.isPending} onClick={() => { setInfo(null); extractMut.mutate(); }}>
                Extract from invoice
              </Button>
            </Stack>
            {info && <Alert severity="info" sx={{ mt: 1, py: 0 }}>{info}</Alert>}
          </Box>

          <Divider textAlign="left"><Typography variant="caption" color="text.secondary">Line items (review & edit)</Typography></Divider>
          <Table size="small">
            <TableHead><TableRow>
              <TableCell>Item name *</TableCell><TableCell>Category</TableCell><TableCell>Brand</TableCell><TableCell>UOM</TableCell>
              <TableCell>HSN</TableCell><TableCell align="right">GST%</TableCell><TableCell align="right">Qty</TableCell><TableCell align="right">Rate</TableCell><TableCell align="right">Amount</TableCell><TableCell />
            </TableRow></TableHead>
            <TableBody>
              {lines.map((l, i) => (
                <TableRow key={i}>
                  <TableCell><TextField size="small" variant="standard" fullWidth value={l.itemName} onChange={(e) => setLine(i, 'itemName', e.target.value)} /></TableCell>
                  <TableCell><TextField size="small" variant="standard" value={l.category ?? ''} onChange={(e) => setLine(i, 'category', e.target.value)} sx={{ width: 100 }} /></TableCell>
                  <TableCell><TextField size="small" variant="standard" value={l.brand ?? ''} onChange={(e) => setLine(i, 'brand', e.target.value)} sx={{ width: 100 }} /></TableCell>
                  <TableCell><TextField size="small" variant="standard" value={l.uom ?? ''} onChange={(e) => setLine(i, 'uom', e.target.value)} sx={{ width: 60 }} /></TableCell>
                  <TableCell><TextField size="small" variant="standard" value={l.hsnCode ?? ''} onChange={(e) => setLine(i, 'hsnCode', e.target.value)} sx={{ width: 80 }} /></TableCell>
                  <TableCell align="right"><TextField size="small" variant="standard" type="number" value={l.gstPercent ?? ''} onChange={(e) => setLine(i, 'gstPercent', e.target.value === '' ? null : Number(e.target.value))} sx={{ width: 56 }} /></TableCell>
                  <TableCell align="right"><TextField size="small" variant="standard" type="number" value={l.quantity} onChange={(e) => setLine(i, 'quantity', Number(e.target.value))} sx={{ width: 64 }} /></TableCell>
                  <TableCell align="right"><TextField size="small" variant="standard" type="number" value={l.rate ?? ''} onChange={(e) => setLine(i, 'rate', e.target.value === '' ? null : Number(e.target.value))} sx={{ width: 80 }} /></TableCell>
                  <TableCell align="right">{inr((Number(l.quantity) || 0) * (Number(l.rate) || 0))}</TableCell>
                  <TableCell><IconButton size="small" color="error" onClick={() => setLines((arr) => arr.filter((_, j) => j !== i))} disabled={lines.length === 1}><Delete fontSize="small" /></IconButton></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Button size="small" startIcon={<Add />} onClick={() => setLines((arr) => [...arr, emptyLine()])}>Add line</Button>
            <Typography variant="body2" color="text.secondary">Materials: {inr(lineTotal)}</Typography>
          </Stack>

          <Divider textAlign="left"><Typography variant="caption" color="text.secondary">Other charges — freight, transport, packing, etc. (not stock)</Typography></Divider>
          {validCharges.length === 0 && charges.length === 0 && (
            <Typography variant="caption" color="text.secondary">No freight/other charges. Add any charge that isn't a material.</Typography>
          )}
          {charges.length > 0 && (
            <Table size="small">
              <TableHead><TableRow><TableCell>Charge</TableCell><TableCell align="right">GST%</TableCell><TableCell align="right">Amount</TableCell><TableCell /></TableRow></TableHead>
              <TableBody>
                {charges.map((c, i) => (
                  <TableRow key={i}>
                    <TableCell><TextField size="small" variant="standard" fullWidth placeholder="e.g. Freight & Charges" value={c.label} onChange={(e) => setCharge(i, 'label', e.target.value)} /></TableCell>
                    <TableCell align="right"><TextField size="small" variant="standard" type="number" value={c.gstPercent ?? ''} onChange={(e) => setCharge(i, 'gstPercent', e.target.value === '' ? null : Number(e.target.value))} sx={{ width: 56 }} /></TableCell>
                    <TableCell align="right"><TextField size="small" variant="standard" type="number" value={c.amount ?? ''} onChange={(e) => setCharge(i, 'amount', e.target.value === '' ? null : Number(e.target.value))} sx={{ width: 110 }} /></TableCell>
                    <TableCell><IconButton size="small" color="error" onClick={() => setCharges((arr) => arr.filter((_, j) => j !== i))}><Delete fontSize="small" /></IconButton></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
          <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
            <Button size="small" startIcon={<Add />} onClick={() => setCharges((arr) => [...arr, emptyCharge()])}>Add charge</Button>
            <Stack alignItems="flex-end" spacing={0.25}>
              {chargesTotal > 0 && <Typography variant="body2" color="text.secondary">Charges: {inr(chargesTotal)}</Typography>}
              <Typography variant="body2" color="text.secondary">Sub-total: {inr(subTotal)}</Typography>
              <Typography variant="body2" color="text.secondary">GST: {inr(taxTotal)}</Typography>
              <Typography variant="subtitle1">Grand Total: {inr(total)}</Typography>
            </Stack>
          </Stack>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" disabled={!canSave || saveMut.isPending} onClick={() => saveMut.mutate()}>Save & Receive Stock</Button>
      </DialogActions>
    </Dialog>
  );
}

function DetailDialog({ id, onClose }: { id: number; onClose: () => void }) {
  const { data: p } = useQuery({ queryKey: ['inv-purchase', id], queryFn: () => purchasesApi.get(id) });
  if (!p) return null;
  return (
    <Dialog open fullWidth maxWidth="md" onClose={onClose}>
      <DialogTitle>{p.purchaseCode}</DialogTitle>
      <DialogContent dividers>
        <Typography variant="body2" color="text.secondary" gutterBottom>
          {p.supplierName ?? '—'}{p.supplierGstin ? ` (${p.supplierGstin})` : ''} · Invoice {p.invoiceNumber ?? '—'} · {p.invoiceDate ? new Date(p.invoiceDate).toLocaleDateString('en-IN') : '—'} · Total {inr(p.totalAmount)}
          {p.invoiceUrl ? <> · <a href={p.invoiceUrl} target="_blank" rel="noreferrer">Invoice</a></> : null}
        </Typography>

        {(p.shipFrom || p.shipTo || p.transporterName || p.vehicleNo || p.lrNumber || p.ewayBillNo || p.dispatchDate) && (
          <Box sx={{ bgcolor: 'action.hover', borderRadius: 1.5, p: 1.5, mb: 2 }}>
            <Typography variant="caption" color="text.secondary" display="block" gutterBottom>Shipment / Freight</Typography>
            <Typography variant="body2">
              <strong>{p.shipFrom ?? '—'}</strong> → <strong>{p.shipTo ?? '—'}</strong>
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {[p.transporterName && `Transporter: ${p.transporterName}`, p.vehicleNo && `Vehicle: ${p.vehicleNo}`,
                p.lrNumber && `LR: ${p.lrNumber}`, p.ewayBillNo && `E-Way: ${p.ewayBillNo}`,
                p.dispatchDate && `Dispatched: ${new Date(p.dispatchDate).toLocaleDateString('en-IN')}`].filter(Boolean).join(' · ') || '—'}
            </Typography>
          </Box>
        )}

        <Table size="small">
          <TableHead><TableRow><TableCell>Item</TableCell><TableCell>Brand</TableCell><TableCell>UOM</TableCell><TableCell align="right">Qty</TableCell><TableCell align="right">Rate</TableCell><TableCell align="right">Amount</TableCell><TableCell>New?</TableCell></TableRow></TableHead>
          <TableBody>
            {(p.items ?? []).map((i) => (
              <TableRow key={i.id}>
                <TableCell>{i.itemName}</TableCell><TableCell>{i.brand ?? '—'}</TableCell><TableCell>{i.uom ?? '—'}</TableCell>
                <TableCell align="right">{i.quantity}</TableCell><TableCell align="right">{inr(i.rate ?? 0)}</TableCell><TableCell align="right">{inr(i.amount)}</TableCell>
                <TableCell>{i.itemCreated ? <Chip size="small" color="success" label="new" /> : <Chip size="small" variant="outlined" label="reused" />}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>

        {(p.charges?.length ?? 0) > 0 && (
          <>
            <Typography variant="caption" color="text.secondary" sx={{ mt: 2, display: 'block' }}>Other charges (freight etc. — not stock)</Typography>
            <Table size="small">
              <TableBody>
                {(p.charges ?? []).map((c) => (
                  <TableRow key={c.id}>
                    <TableCell>{c.label}</TableCell>
                    <TableCell align="right">{inr(c.amount ?? 0)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </>
        )}
      </DialogContent>
      <DialogActions><Button onClick={onClose}>Close</Button></DialogActions>
    </Dialog>
  );
}
